import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { LANDING } from "@/constants/testIds";
import { fetchConfig, createCheckoutSession } from "@/lib/api";
import Layout from "@/components/layout/Layout";

// What's inside a bundled product, shown as a breakdown under its card.
// Keyed by product id; add an entry when a new bundle is introduced.
const PRODUCT_INCLUDES = {
  "identity-shift-collection": [
    "Book 1: The Pattern of You",
    "Book 2: The Probability of You",
    "Book 3: The Inheritance of You",
    "Wealth Journal (30-Day Practice)",
    "Success Journal (30-Day Practice)",
    "Health Journal (30-Day Practice)",
    "Relationships Journal (30-Day Practice)",
    "The Daily Practice Kit",
  ],
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("product");

  const [config, setConfig] = useState(null);
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  const products = useMemo(() => config?.products ?? [], [config]);
  const currency = config?.currency ?? "USD";

  useEffect(() => {
    fetchConfig()
      .then((data) => { setConfig(data); setConfigReady(true); })
      .catch(() => setConfigReady(true));
  }, []);

  // Once the catalog loads, pre-select the product from ?product=, else the first.
  useEffect(() => {
    if (!products.length) return;
    setSelected((prev) => {
      if (prev.size) return prev;
      const next = new Set();
      const match = products.find((p) => p.id === preselect);
      next.add(match ? match.id : products[0].id);
      return next;
    });
  }, [products, preselect]);

  const fmt = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n ?? 0);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedProducts = products.filter((p) => selected.has(p.id));
  const total = selectedProducts.reduce((sum, p) => sum + (p.price || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProducts.length) {
      toast.error("Please select at least one product.");
      return;
    }
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      if (!configReady) await fetchConfig();
      const data = await createCheckoutSession({
        email,
        items: selectedProducts.map((p) => ({ id: p.id, quantity: 1 })),
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Toaster position="top-center" theme="dark" />
      <section className="container section-padding">
        <button
          onClick={() => navigate(-1)}
          data-testid={LANDING.checkoutBack}
          style={{ fontSize: "0.75rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", marginBottom: "40px" }}
        >
          ← Back
        </button>

        <div className="checkout-grid">
          {/* Left: choose products */}
          <div>
            <div className="hero-kicker">Your Order</div>
            <h1 style={{ fontSize: "2.6rem", margin: "8px 0" }}>Choose Your Products</h1>
            <p style={{ color: "var(--text-secondary)", marginBottom: "24px", maxWidth: "48ch" }}>
              Select one or both. Digital products (PDF) — instant download, lifetime access.
            </p>

            {!configReady && !products.length ? (
              <p style={{ color: "var(--text-muted)" }}>Loading products…</p>
            ) : null}

            {products.map((product) => {
              const isSelected = selected.has(product.id);
              const includes = PRODUCT_INCLUDES[product.id];
              return (
                <div
                  key={product.id}
                  style={{
                    marginBottom: "16px",
                    border: isSelected ? "1px solid var(--gold-warm)" : "1px solid var(--border-glass)",
                    borderRadius: "var(--radius-md)",
                    padding: "20px 24px",
                    background: isSelected ? "rgba(201,162,39,0.06)" : "transparent",
                    transition: "all 0.3s ease",
                  }}
                >
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "16px", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(product.id)}
                      aria-label={`Add ${product.title}`}
                      style={{ marginTop: "5px", width: "20px", height: "20px", accentColor: "var(--gold-warm)", cursor: "pointer", flex: "0 0 auto" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "baseline" }}>
                        <h3 style={{ fontSize: "1.3rem", margin: 0 }}>{product.title}</h3>
                        <span className="price-main" style={{ fontSize: "1.4rem", whiteSpace: "nowrap" }}>{fmt(product.price)}</span>
                      </div>
                      {product.subtitle ? (
                        <p style={{ fontSize: "0.925rem", color: "var(--text-secondary)", lineHeight: 1.6, marginTop: "6px" }}>
                          {product.subtitle}
                        </p>
                      ) : null}

                      {includes && includes.length ? (
                        <ul className="stack-list" style={{ marginTop: "14px" }}>
                          {includes.map((item) => (
                            <li key={item}><span>{item}</span></li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>

          {/* Right: pay */}
          <div className="checkout-pay">
            <div className="price-anchor-box" style={{ padding: "32px" }}>
              <div className="hero-kicker" style={{ marginBottom: "20px" }}>Secure Checkout</div>
              <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginBottom: "16px" }}>
                  <label htmlFor="checkout-email" style={{ display: "block", fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: "6px" }}>Email for delivery</label>
                  <input
                    id="checkout-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid={LANDING.checkoutEmailInput}
                    style={{ width: "100%", padding: "12px 16px", borderRadius: "4px", background: "rgba(7,17,31,0.8)", border: "1px solid var(--border-glass)", color: "#FFF", fontSize: "1rem" }}
                  />
                </div>

                <div style={{ borderTop: "1px solid var(--border-glass)", paddingTop: "16px", marginTop: "16px" }}>
                  {selectedProducts.length ? (
                    selectedProducts.map((p) => (
                      <div key={p.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.925rem", color: "var(--text-secondary)" }}>
                        <span>{p.title}</span>
                        <span>{fmt(p.price)}</span>
                      </div>
                    ))
                  ) : (
                    <p style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>No products selected yet.</p>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderTop: "1px solid var(--border-glass)", paddingTop: "16px", margin: "16px 0" }}>
                  <span style={{ fontSize: "0.8rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>Total</span>
                  <span data-testid={LANDING.checkoutTotal} className="price-main" style={{ fontSize: "2.4rem" }}>
                    {fmt(total)}
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={loading || !selectedProducts.length}
                  data-testid={LANDING.checkoutSubmit}
                  className="btn btn-primary-gold btn-lg"
                  style={{ width: "100%", marginTop: "16px", opacity: loading || !selectedProducts.length ? 0.6 : 1 }}
                >
                  {loading ? "Starting checkout…" : "Pay securely →"}
                </button>

                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", lineHeight: 1.5, marginTop: "12px", textAlign: "center" }}>
                  Secure payment processed by Dodo Payments. Download links arrive by email and on the next screen.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
